import { DestroyRef, Injector, runInInjectionContext } from '@angular/core';
import { getModelId, matchContextGroups } from '@core/shared/model';
import { ConfigService } from '@core/shared/config';
import { Store } from '@ngxs/store';
import { convertToSubject } from '@core/shared/utils';
import { BehaviorSubject, combineLatest, map, of, take } from 'rxjs';
import { AccessState } from '../access';
import { DevToolsRegistry } from '../dev-tools';
import { FeatureService } from '../feature';
import { simpleAbilityCheck } from '@core/shared/ability';
import { ModelActionHelper } from './model-action.helper';
import { injectorWithProviders, ManualDestroyRef } from '../utils';
import * as i0 from "@angular/core";
import * as i1 from "../dev-tools";
import * as i2 from "@core/shared/config";
import * as i3 from "@ngxs/store";
import * as i4 from "../feature";
/**
 * model actions can be registered through FeatureDefinitions or by injecting ModelActionService and using the registerModelAction method.
 */
export let ModelActionService = /*#__PURE__*/(() => {
  class ModelActionService {
    constructor(debug, config, store, feature, injector) {
      this.store = store;
      this.injector = injector;
      this.customModelActions$ = new BehaviorSubject([]);
      this.platform = config.getPlatform();
      this.featureBasedModelActions$ = feature.getLoadedFeatures().pipe(map(features => {
        return features.reduce((allActions, f) => {
          const actions = f.getFeatureFunctionalityCached().itemActions;
          if (actions) allActions.push(...actions);
          return allActions;
        }, []);
      }), convertToSubject());
      debug.exposeDebugFunction(['Model', 'Inspect ModelActions'], () => {
        return combineLatest({
          featureBased: this.featureBasedModelActions$,
          custom: this.customModelActions$
        });
      });
    }
    registerModelAction(modelIdInput, action) {
      const id = getModelId(modelIdInput);
      action.modelId = id;
      this.customModelActions$.next([...this.customModelActions$.value, action]);
    }
    getUnfilteredActionsForModelId(modelIdInput) {
      const id = getModelId(modelIdInput);
      return combineLatest({
        featureBased: this.featureBasedModelActions$,
        custom: this.customModelActions$
      }).pipe(map(({
        featureBased,
        custom
      }) => {
        const all = [...featureBased, ...custom];
        return all.filter(action => {
          return action.modelId === id;
        });
      }));
    }
    getModelActions(modelId, mode, customGroups = [], customActions) {
      // atm there is no sense in allowing itemActions to be available on non-existing items.
      // so we only return model actions if the mode is list view (many existing items) or edit (one existing item).
      const modesAllowedForModelActions = ['list', 'edit', 'view'];
      if (!modesAllowedForModelActions.includes(mode)) {
        return of([]);
      }
      const abilities = this.store.selectSnapshot(AccessState.abilities);
      const role = this.store.selectSnapshot(AccessState.role);
      const currentContext = [this.platform, ...customGroups];
      return this.getUnfilteredActionsForModelId(modelId).pipe(take(1), map(actions => {
        if (customActions) actions = [...actions, ...customActions];
        const allowed = actions.filter(action => {
          const contextAllowed = !action.displayContext || matchContextGroups(currentContext, action.displayContext);
          const abilityCheck = !action.simpleAbilityCheck || simpleAbilityCheck(role, abilities, action.simpleAbilityCheck);
          return contextAllowed && abilityCheck;
        });
        return allowed.map(config => {
          const inner = config.action;
          return {
            ...config
          };
        });
      }));
    }
    createMenuItemsFromItemModelActions$(contextMenuSelection$, contextMenuActionsAll$, customInjector) {
      return combineLatest({
        selection: contextMenuSelection$,
        itemActions: contextMenuActionsAll$
      }).pipe(map(({
        selection,
        itemActions
      }) => {
        if (!itemActions?.length) return [];
        if (!selection) return [];
        const menuItems = this.createMenuItemsFromItemModelActions(itemActions, () => selection, customInjector);
        return menuItems;
      }));
    }
    createMenuItemsFromItemModelActions(itemActions, getSelectedItem, customInjector) {
      if (!itemActions) return [];
      const transformedItems = itemActions.map(action => {
        const item = getSelectedItem();
        return {
          ...ModelActionHelper.processItemActionProperties(action, item),
          automationId: action.id || undefined,
          disabled: action.enableIf && !action.enableIf(item),
          command: () => {
            const currentItem = getSelectedItem();
            if (!currentItem) throw new Error('ItemAction failed: No selected item available!');
            const destroyRef = new ManualDestroyRef();
            const inj = injectorWithProviders(customInjector || this.injector, [{
              provide: DestroyRef,
              useValue: destroyRef
            }], 'temporaryItemActionContext');
            runInInjectionContext(inj, () => {
              action.action(currentItem);
            });
            destroyRef.destroy();
          }
        };
      });
      return transformedItems;
    }
    static {
      this.ɵfac = function ModelActionService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ModelActionService)(i0.ɵɵinject(i1.DevToolsRegistry), i0.ɵɵinject(i2.ConfigService), i0.ɵɵinject(i3.Store), i0.ɵɵinject(i4.FeatureService), i0.ɵɵinject(i0.Injector));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: ModelActionService,
        factory: ModelActionService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ModelActionService;
})();